import { z } from 'zod';
import { timestampSchema, uuidSchema } from './common';
import { pageSlugSchema } from './pages';
import { toolsetConnectionBindingSchema } from './rekor-toolset';

/**
 * An open team page's watch (wayai-pro/wayai#5333 Phase 8, decision 13's live refresh): while a viewer
 * holds a team page open, it watches the bases its declared tools reach, so the `base_changed` frames
 * for those bases reach the viewer even when no org grant would send them. The frames are ids only
 * (`rekor-base-change.ts`); a page refetches through its own bridge reads.
 *
 * `PUT /api/hubs/:hub_id/pages/:slug/watch/:watch_id` starts or renews a watch: when the page mounts,
 * and again whenever its live socket reconnects. `DELETE` on the same path ends it when the page
 * unmounts. `watch_id` is the client's own id for one mount of the page (one tab's copy of it). Only a
 * person's own sign-in may, and only on a hub whose team pages admit them; a `PUT` only for a team page
 * offered to them, as for the page's read.
 */

/** `PUT` / `DELETE /api/hubs/:hub_id/pages/:slug/watch/:watch_id`. */
export const hubPageWatchParam = z.object({ hub_id: uuidSchema, slug: pageSlugSchema, watch_id: uuidSchema });

/** A base a watched page's declared tools reach, as its connection's toolset binding records it. */
export const pageWatchedBaseSchema = toolsetConnectionBindingSchema.pick({ base_id: true, environment: true });
export type PageWatchedBase = z.infer<typeof pageWatchedBaseSchema>;

/**
 * `PUT /api/hubs/:hub_id/pages/:slug/watch/:watch_id`: the bases the page's declared tools reach now,
 * whose `base_changed` frames the page refetches on, the hub's org, and when the watch lapses unless
 * renewed. No bases: the page's tools reach no base right now.
 *
 * `org_id` is the hub's org, in every answer. A base id is unique only within its org, so a frame is the
 * page's only when its `org_id`, `base_id` and `environment` all match (#5333 row 8.1B2): a viewer who is
 * also a member of another org receives that org's frames too. An answer without `org_id` (a backend
 * before row 8.1B2) does not parse, so a page holding one matches no frame by it.
 *
 * `expires_at` is null when no watch was recorded: the viewer is a member of the hub's org, whom every
 * frame of the org reaches already, or holds no admin or team row on the hub (admitted by a grant alone,
 * as a platform admin outside the org is), so no frame reaches them through a watch.
 *
 * Mobile backward compatibility: `org_id` was added to an existing answer, and the mobile app does not
 * call this route.
 */
export const hubPageWatchResponseSchema = z.object({
  data: z.object({ bases: z.array(pageWatchedBaseSchema), expires_at: timestampSchema.nullable(), org_id: uuidSchema }),
});
export type HubPageWatchResponse = z.infer<typeof hubPageWatchResponseSchema>;

/** `DELETE /api/hubs/:hub_id/pages/:slug/watch/:watch_id`: the base rows the watch held (0 when none). */
export interface HubPageUnwatchResponse {
  data: { removed: number };
}
